import React, {useState} from 'react';
import {message, Popover} from "antd";
import '../mainLayour.css';
import {HelpCircle, Settings, LogOut} from 'lucide-react';
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import { useTranslation } from "react-i18next";
import { clearAccessToken } from "../../../utils/accessToken";
import { logout_user, update_current_user_profile } from "../../../Services/user.service";
import { ReviewActionTypes, UserActionTypes } from "../../../Actions/actionTypes";
import ProfileSettingsModal from "./ProfileSettingsModal";
import UsagePanel from "../../stage-review/commercial/UsagePanel";
import { clearCommercialControl, fetchCommercialUsage } from "../../../Actions/commercialControl.action";
import { usePageTransition } from "../../../utils/pageTransition";
import { clearAllAiTasks } from "../../../Reducers/aiTasksReducer";

const UserInfoHeader = () => {
    const navigate = useNavigate();
    const { confirmAiNavigationIfNeeded } = usePageTransition();
    const dispatch = useDispatch();
    const userState = useSelector((state) => state.users);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const currentUser = userState.user?.id ? userState.user : userState.currentUser;
    const canManageBilling = ["SUPER_ADMIN", "ADMIN_CLIENT"].includes(currentUser?.role);
    const billingReturnPath = currentUser?.role === "SUPER_ADMIN"
        ? "/admin/commercial?tab=subscriptions"
        : "/workspace";
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [usagePanelOpen, setUsagePanelOpen] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    // Utilisation des namespaces
    const { t: tNav } = useTranslation('navigation');
    const { t: tCommon } = useTranslation('common');
    const { t: tUsers } = useTranslation('users');

    if (!currentUser?.id) return null;

    const fullName = `${currentUser.name} ${currentUser.surname || ''}`.trim();
    const clientName = currentUser.client_name || tCommon('general.unknown_client');

    const handleLogout = async () => {
        const confirmed = await confirmAiNavigationIfNeeded();
        if (!confirmed) return;

        try {
            await logout_user(); // Révoque la session refresh côté backend
        } catch (e) {
            // Ignorer les erreurs pour ne pas bloquer l'utilisateur
        } finally {
            sessionStorage.removeItem("user_id");
            sessionStorage.removeItem("nom_complet");
            sessionStorage.removeItem("verdictia_auth_fade");
            clearAccessToken();
            dispatch({ type: ReviewActionTypes.CLEAR_ANALYSES_SUMMARY });
            dispatch(clearAllAiTasks());
            dispatch(clearCommercialControl());
            dispatch({ type: UserActionTypes.SET_USER, payload: {} });
            dispatch({ type: UserActionTypes.SET_CURRENT_USER, payload: {} });
            dispatch({ type: UserActionTypes.SET_AUTH, payload: false });
            navigate("/");
        }
    };

    const openSettings = () => {
        setMenuOpen(false);
        setSettingsOpen(true);
    };

    const refreshCommercialUsage = () => {
        const result = dispatch(fetchCommercialUsage());
        if (result?.catch) {
            result.catch(() => {});
        }
        return result;
    };

    const openUsagePanel = () => {
        setUsagePanelOpen(true);
        refreshCommercialUsage();
    };

    const handleSettingsSave = async (values) => {
        setSavingSettings(true);
        try {
            const result = await update_current_user_profile({
                audience_profile: values.audience_profile,
            });
            const updatedUser = result?.data;
            if (updatedUser) {
                dispatch({ type: UserActionTypes.SET_CURRENT_USER, payload: updatedUser });
                dispatch({ type: UserActionTypes.SET_USER, payload: updatedUser });
            }
            message.success(tUsers("profileSettings.success"));
            setSettingsOpen(false);
        } catch (err) {
            message.error(
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                tUsers("profileSettings.error")
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const content = (
        <div className="user-menu">
            <p className="user-menu-item">
                <HelpCircle size={16} className="menu-icon"/>
                {tNav('help')}
            </p>
            <p className="user-menu-item" onClick={openSettings}>
                <Settings size={16} className="menu-icon"/>
                {tNav('settings')}
            </p>
            <p className="user-menu-item" onClick={handleLogout}>
                <LogOut size={16} className="menu-icon"/>
                {tNav('logout')}
            </p>
        </div>
    );

    const initials = fullName
        .split(' ')
        .map(word => word[0]?.toUpperCase())
        .slice(0, 2)
        .join('');

    return (
        <>
            <Popover
                placement="bottomLeft"
                title={tNav('menu')}
                content={content}
                trigger="click"
                open={menuOpen}
                onOpenChange={setMenuOpen}
            >
                {/* ... reste du composant ... */}
                <div
                    className="header-user-trigger"
                    style={{
                        cursor: "pointer",
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: 'white',
                        marginRight: "10px"
                    }}
                >
                    <div
                        className="header-user-avatar"
                        style={{
                            backgroundColor: `hsl(${Math.abs(fullName.split('').reduce((a,b)=>(a<<5)-a+b.charCodeAt(0)|0, 0)) % 360}, 70%, 60%)`,
                            color: 'white',
                            width: '35px',
                            height: '35px',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            cursor: "pointer"
                        }}
                    >
                        {initials}
                    </div>
                    <div className="header-user-details" style={{lineHeight: '1.2', margin: '2px'}}>
                        <div className="header-user-name" style={{fontWeight: 'bold', fontSize: '12px', marginBottom: "4px"}}>{fullName}</div>
                        <div className="header-user-client" style={{fontSize: '12px', opacity: 0.8}}>{clientName}</div>
                    </div>
                </div>
            </Popover>
            <ProfileSettingsModal
                open={settingsOpen}
                currentUser={currentUser}
                saving={savingSettings}
                onCancel={() => setSettingsOpen(false)}
                onSubmit={handleSettingsSave}
                plan={commercialControl.plan}
                usageByType={commercialControl.usageByType}
                isUsageLoading={commercialControl.isLoadingUsage}
                onOpenUsage={openUsagePanel}
            />
            <UsagePanel
                open={usagePanelOpen}
                onClose={() => setUsagePanelOpen(false)}
                usageSnapshot={commercialControl.usageSnapshot}
                usageByType={commercialControl.usageByType}
                capabilities={commercialControl.capabilities}
                plan={commercialControl.plan}
                isLoading={commercialControl.isLoadingUsage}
                error={commercialControl.lastCommercialError}
                onRefresh={refreshCommercialUsage}
                canManageBilling={canManageBilling}
                billingReturnPath={billingReturnPath}
            />
        </>
    );
};

export default UserInfoHeader;
